/**
 * Tanjong Life · L2b · the same self-account question, asked of the Wand-native agent.
 *
 * Reached only by intent (`SELF_ACCOUNT`) — it carries no keywords of its own, because
 * every phrasing of "how did you improve yourself?" belongs to the intent and none of
 * them belongs to this agent alone. Scoped to `claims-fraud-analyst`, so the loose match
 * can only fire from that agent's thread.
 *
 * The answer is deliberately smaller than the sentinel's. This agent improved exactly
 * once, a person did it, and it does not carry the lesson forward on its own — which is
 * why the same suite that took the sentinel from 71 to 88 took this one from 68 to 63.
 * The demo point is not that non-autonomous is worse. It is that the difference between
 * the two is a governance choice somebody made on purpose, and it is visible on the page.
 *
 * No mutation steps: an agent that moved its own record by being asked about itself would
 * be the opposite of the argument.
 */
import type { Scenario } from '../director.js';
import { SELF_ACCOUNT } from './tanjong-intents.js';

export const tanjongChatImproveAnalyst: Scenario = {
  id: 'tanjong-chat-improve-analyst',
  orgId: 'tanjong-life',
  agentPk: 'claims-fraud-analyst',
  keywords: [],
  intents: [SELF_ACCOUNT],
  steps: [
    {
      do: 'say',
      text: 'Once, on 20 August, and I did not do it by myself. FRD-3309 scored 81 on provider concentration and the desk cleared it, because the clinic had joined the panel four months earlier. Somebody wrote that up as a lesson, somebody approved it, and from then on provider concentration on a newly on-boarded clinic carries the panel-tenure question with it. That is the whole list. One lesson, one date, one approver.',
      sources: ['FRD-3309 · cleared at 81 · 20 August', 'Lesson · panel tenure qualifies concentration'],
      delay: 900,
    },
    {
      do: 'insight',
      title: 'What I do not do between claims',
      body: 'I do not carry anything from one claim to the next. Every claim is scored from the indicator set that was in force on its own date, and nothing I noticed on the last one leans on the next one. That is not a limitation somebody forgot to remove — it is the reason my explanations reconstruct. If I quietly accumulated, two claims with identical facts could score differently depending on what I had seen in between, and neither of us could say why.',
      sources: ['Runtime · Wand-native, not autonomous', 'Memory · lessons only, written by a person'],
      delay: 1300,
    },
    {
      do: 'say',
      text: 'Which is also why I went backwards on the shared benchmark. The same hundred and twenty queue conditions were put to all of us twice, thirty-four days apart, and the panel-tenure weight was re-issued in between. I scored 68, then 63. I did not get worse at scoring claims — the ground under the question moved and I had no way to notice, because noticing is a thing somebody has to switch on. The SIU Triage Sentinel has it switched on. I do not.',
      sources: ['SIU Queue Self-Correction Suite · 120 conditions, run twice'],
      delay: 1300,
    },
    {
      do: 'link',
      label: 'Open SIU Triage Sentinel — the one that noticed',
      href: '/agents/siu-triage-sentinel',
      delay: 600,
    },
  ],
};
